<template>
  <div class="followup-wrapper">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <h4 class="loading-title">{{ dynamicTitle }} - טעינה</h4>
      <progress class="progress is-primary" max="100"></progress>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!hasAnyData" class="no-data-container">
      <h4 class="no-data-title">{{ dynamicTitle }}</h4>
      <div class="no-data-message">
        <p>⚠️ לא נמצאו נתונים מתאימים עבור משימה זו</p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="followup-container">
      <!-- Header with Dynamic Title and Timer -->
      <div class="followup-header">
        <h4 class="followup-title">{{ dynamicTitle }}</h4>

        <!-- Timer Display -->
        <div class="timer-display" :class="{'timer-red': isTimerRed, 'timer-blue': !isTimerRed}">
          <span class="timer-icon">⏱</span>
          <span class="timer-text">{{ formattedTimer }}</span>
        </div>
      </div>

      <!-- Compact Content Section -->
      <div class="followup-content">
        <!-- Company Info -->
        <div v-if="company" class="info-item">
          <div class="info-row">
            <link-entity
              :payload="getCompanyPayload()"
              :additional_data="{ resource_type: 'Company' }"
            />
          </div>
        </div>

        <!-- Position Info -->
        <PositionDetailsBlock
          v-if="position"
          :position="position"
        />

        <!-- Applicant Info -->
        <ApplicantDetailsBlock
          v-if="applicant"
          :applicant="applicant"
          :applicant-payload="getApplicantPayload()"
          :ghana-flag-payload="getApplicantPayloadForGhanaFlag()"
          @open-follow-up="openApplicantAlertsModal"
          @applicant-answered="applicantAnswered"
        />

        <OfferDetailsBlock v-if="offer" :offer="offer" :showEditButton="true" />

        <!-- Next Offer Button -->
        <div class="next-offer-section">
          <button
            @click="moveToNextOffer"
            class="button is-primary is-medium next-offer-btn"
            title="טען הצעה הבאה מאותו סוג משימה"
          >
            <span class="icon">⏭</span>
            <span>הצעה הבאה</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity.vue'
import OfferDetailsBlock from '@/components/shared/organisms/OfferDetailsBlock.vue'
import ApplicantDetailsBlock from '@/components/shared/organisms/ApplicantDetailsBlock.vue'
import PositionDetailsBlock from '@/components/shared/organisms/PositionDetailsBlock.vue'
import entityPayloadMixin from '@/mixins/entityPayloadMixin'
import offerValidationMixin from '@/mixins/offerValidationMixin'

export default {
  name: 'FollowUpComponent',
  mixins: [entityPayloadMixin, offerValidationMixin],
  components: {
    LinkEntity,
    OfferDetailsBlock,
    ApplicantDetailsBlock,
    PositionDetailsBlock
  },
  props: {
    alert: {
      type: Object,
      required: false,
      default: () => null
    },
    mondayTaskData: {
      type: Object,
      required: false,
      default: () => null
    },
    selectedTaskName: {
      type: String,
      required: false,
      default: null
    }
  },
  computed: {
    dynamicTitle () {
      // Use the prop passed from parent (FeedAlert)
      if (this.selectedTaskName) {
        return `🔔 ${this.selectedTaskName}`
      }
      return '🔔 Follow Up'
    },
    offer () {
      return this.alert && this.alert.resources && this.alert.resources.offer
    },
    applicant () {
      return this.alert && this.alert.resources && this.alert.resources.applicant
    },
    position () {
      return this.alert && this.alert.resources && this.alert.resources.position
    },
    company () {
      return this.alert && this.alert.resources && this.alert.resources.company
    },
    hasAnyData () {
      return !!(this.offer || this.applicant || this.position || this.company)
    },
    formattedTimer () {
      const mins = Math.floor(this.timerSeconds / 60)
      const secs = this.timerSeconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
    isTimerRed () {
      return this.timerSeconds >= 40
    }
  },
  watch: {
    alert: {
      handler (newAlert, oldAlert) {
        // Reset timer when new alert arrives
        if (newAlert && (!oldAlert || newAlert.id !== oldAlert.id)) {
          this.resetTimer()
        }
      },
      deep: true
    }
  },
  data () {
    return {
      isLoading: false,
      // Timer for each offer
      timerSeconds: 0,
      timerInterval: null
    }
  },
  mounted () {
    console.log('FollowUp component mounted')
    console.log('Alert:', this.alert)
    console.log('Monday task data:', this.mondayTaskData)

    // Debug: Check if entities exist
    console.log('=== FOLLOW UP ENTITIES DEBUG ===')
    console.log('Offer exists:', !!this.offer, this.offer)
    console.log('Applicant exists:', !!this.applicant, this.applicant)
    console.log('Position exists:', !!this.position, this.position)
    console.log('Company exists:', !!this.company, this.company)
    console.log('=================================')

    if (this.alert && this.alert.resources) {
      console.log('Alert resources:', this.alert.resources)
    }

    // Start timer for this offer
    this.startTimer()
  },
  beforeUnmount () {
    this.stopTimer()
  },
  methods: {
    ...mapMutations('modals', ['create', 'remove']),

    openApplicantAlertsModal () {
      if (!this.applicant) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד',
          type: 'warning'
        })
        return
      }

      const applicantData = {
        ...this.applicant,
        resource_id: this.applicant.resource_id || this.applicant.id
      }

      this.$store.commit('modals/create', {
        designator: 'resource-alerts-form-modal',
        component_name: 'ApplicantAlertsForm',
        modal_width: 2000,
        is_center_modal: true,
        data: applicantData,
        resource_type: 'Applicant',
        modal_title: `Alerts - ${this.$utils.getResourceFullName({ resource_type: 'Applicant', data: applicantData })} ${applicantData.phone_number || ''}`
      })
    },

    // Follow-up Action Methods

    async handleBump () {
      if (!this.offer) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני הצעה',
          type: 'warning'
        })
        return
      }

      try {
        const today = new Date().toLocaleDateString('he-IL')
        const newMessage = (this.offer.message || '') + `\nהוקפץ במייל ביום ${today}`

        // Update offer message
        await this.$network_utils.updateOffer({
          id: this.offer.id,
          message: newMessage
        })

        // Update alert timing (7 days from now)
        if (this.alert && this.alert.id) {
          const nextWeek = new Date()
          nextWeek.setDate(nextWeek.getDate() + 7)
          await this.$network_utils.updateAlert({
            id: this.alert.id,
            timing: nextWeek.toISOString()
          })
        }

        // Create stat
        await this.$network_utils.postStat({
          resource_type: 'Offer',
          resource_id: this.offer.id,
          designator: 'follow_up_bump',
          content: `הוקפץ במייל ביום ${today}`,
          owner: this.$utils.getOwner().name
        })

        this.$utils.emit('show_toast', {
          text: 'הקפצה נוספה בהצלחה',
          type: 'success'
        })

        // Refresh the component data
        this.$emit('refresh')
      } catch (error) {
        console.error('Error in handleBump:', error)
        this.$utils.emit('show_toast', {
          text: 'שגיאה בהוספת הקפצה',
          type: 'error'
        })
      }
    },

    async handleResend () {
      if (!this.offer) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני הצעה',
          type: 'warning'
        })
        return
      }

      try {
        const today = new Date().toLocaleDateString('he-IL')
        const newMessage = (this.offer.message || '') + `\nנשלח שוב ביום ${today}`

        // Update offer message
        await this.$network_utils.updateOffer({
          id: this.offer.id,
          message: newMessage
        })

        // Create stat
        await this.$network_utils.postStat({
          resource_type: 'Offer',
          resource_id: this.offer.id,
          designator: 'cv_resent',
          content: `נשלח שוב ביום ${today}`,
          owner: this.$utils.getOwner().name
        })

        // Update alert timing (7 days from now)
        if (this.alert && this.alert.id) {
          const nextWeek = new Date()
          nextWeek.setDate(nextWeek.getDate() + 7)
          await this.$network_utils.updateAlert({
            id: this.alert.id,
            timing: nextWeek.toISOString()
          })
        }

        // Open SendCV modal
        this.create({
          designator: 'send-cv-mode-selection-modal',
          component_name: 'SendCvModeSelectionModal',
          modal_width: 1500,
          is_center_modal: true,
          data: this.offer,
          modal_title: 'שליחת קורות חיים מחדש'
        })
      } catch (error) {
        console.error('Error in handleResend:', error)
        this.$utils.emit('show_toast', {
          text: 'שגיאה בפתיחת טופס שליחה',
          type: 'error'
        })
      }
    },

    async handleStopFollowUp () {
      if (!this.offer) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני הצעה',
          type: 'warning'
        })
        return
      }

      // Ask for reason
      this.create({
        designator: 'stop-followup-reason-modal',
        component_name: 'SimpleInputModal',
        modal_width: 500,
        is_center_modal: true,
        modal_title: 'סיום מעקב הצעה',
        props: {
          title: 'למה לא לשלוח שוב?',
          placeholder: 'הזן סיבה לסיום המעקב...',
          onSubmit: async (reason) => {
            try {
              const today = new Date().toLocaleDateString('he-IL')
              const newMessage = (this.offer.message || '') + `\nנסרק ולא נשלח שוב ביום ${today} כי ${reason}`

              // Update offer
              await this.$network_utils.updateOffer({
                id: this.offer.id,
                message: newMessage,
                status_id: 17
              })

              // Update alert to done
              if (this.alert && this.alert.id) {
                await this.$network_utils.updateAlert({
                  id: this.alert.id,
                  status: 'done'
                })
              }

              this.$utils.emit('show_toast', {
                text: 'מעקב הצעה הסתיים בהצלחה',
                type: 'success'
              })

              // Refresh or emit event
              this.$emit('refresh')
            } catch (error) {
              console.error('Error in handleStopFollowUp:', error)
              this.$utils.emit('show_toast', {
                text: 'שגיאה בסיום מעקב',
                type: 'error'
              })
            }
          },
          onCancel: () => {
            console.log('Stop follow-up cancelled')
          }
        }
      })
    },

    handlePostponeFollowUp () {
      if (!this.alert) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני התראה',
          type: 'warning'
        })
        return
      }

      // Open timing modal
      this.create({
        designator: 'postpone-followup-modal',
        component_name: 'FeedAlertEditTiming',
        modal_title: 'דחיית פולואפ',
        is_center_modal: true,
        props: {
          alert: this.alert,
          isSnoozeAll: false,
          onSubmit: (modalId) => {
            this.$utils.emit('show_toast', {
              text: 'תאריך הפולואפ עודכן בהצלחה',
              type: 'success'
            })
            this.$store.commit('modals/removeModalById', modalId)
            this.$emit('refresh')
          }
        }
      })
    },

    // Payload methods provided by entityPayloadMixin

    applicantAnswered () {
      if (!this.applicant) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד',
          type: 'warning'
        })
        return
      }

      this.$network_utils.postStat({
        title: 'Applicant answered phone',
        designator: 'applicant_answered_phone',
        status: 'active',
        owner: this.$utils.getOwner().name,
        priority: 5,
        resource_type: 'Applicant',
        resource_id: this.applicant.resource_id || this.applicant.id
      }).then(() => this.$utils.emit('show_toast', { text: 'המועמד ענה לטלפון - נרשם בהצלחה', type: 'success' }))
    },

    /**
     * Timer Methods
     */
    startTimer () {
      this.stopTimer()
      this.timerSeconds = 0
      this.timerInterval = setInterval(() => {
        this.timerSeconds++
      }, 1000)
    },

    stopTimer () {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },

    resetTimer () {
      this.timerSeconds = 0
      this.startTimer()
    },

    async moveToNextOffer () {
      await this.validateOfferBeforeMove()
    },

    /**
     * Proceed to next offer after validation
     * Overrides mixin method
     */
    async proceedToNextOffer () {
      // Reset timer for next offer
      this.resetTimer()

      // Emit event to parent (FeedAlert) to reload same task type
      this.$emit('reload-same-task')
    }
  }
}
</script>

<style scoped>
.followup-wrapper {
  min-height: 50px;
}

/* Loading State */
.loading-container {
  padding: 1rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 6px;
}

.loading-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

/* No Data State */
.no-data-container {
  padding: 1.5rem;
  text-align: center;
  background: #fff3cd;
  border-radius: 6px;
  border: 1px solid #ffc107;
}

.no-data-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.no-data-message {
  color: #856404;
  font-size: 0.9rem;
}

/* Main Container */
.followup-container {
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

/* Header */
.followup-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.5rem 1rem;
  text-align: center;
}

.followup-title {
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Content Section */
.followup-content {
  padding: 0.75rem;
}

/* Info Items */
.info-item {
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #667eea;
}

.info-item:last-child {
  margin-bottom: 0;
}

/* Info Rows */
.info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  flex-wrap: wrap;
}

.info-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* Message Text */
.message-text {
  background: #ffffff;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-word;
  flex: 1;
}

/* Alert Note (Red Warning) */
.alert-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  background: #ffe5e5;
  border: 1px solid #ff4444;
  border-radius: 4px;
  color: #cc0000;
  font-weight: 600;
  font-size: 0.85rem;
  width: 100%;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.btn-compact {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  color: white;
}

.btn-icon {
  width: 20px;
  height: 20px;
}

.action-icon {
  width: 25px;
  height: 25px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.action-icon:hover {
  transform: scale(1.1);
}

.btn-info {
  background: #3298dc;
}

.btn-info:hover {
  background: #2366d1;
  transform: translateY(-1px);
}

.btn-success {
  background: #48c774;
}

.btn-success:hover {
  background: #3abb67;
  transform: translateY(-1px);
}

/* Icon Sizes */
.followup-container :deep(.operation_icon) {
  max-width: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
}

.followup-container :deep(.is_hot) {
  max-width: 16px !important;
  min-width: 16px !important;
}

.followup-container :deep(.is_watch_image) {
  max-width: 16px !important;
}

.followup-container :deep(img[src="/suitcase.svg"]) {
  width: 14px !important;
}

/* Ensure content wraps properly */
.info-row :deep(*) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* Follow-up Actions Section */
.followup-actions {
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 2px solid #dee2e6;
  margin-top: 0.5rem;
}

.actions-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 0.75rem 0;
  text-align: center;
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);
}

.actions-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.btn-action .icon {
  font-size: 1.2rem;
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-action:active {
  transform: translateY(0);
}

/* Button Colors */
.btn-bump {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  border-color: #2980b9;
}

.btn-bump:hover {
  background: linear-gradient(135deg, #2980b9 0%, #21618c 100%);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
}

.btn-resend {
  background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
  border-color: #229954;
}

.btn-resend:hover {
  background: linear-gradient(135deg, #229954 0%, #1e8449 100%);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
}

.btn-stop {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border-color: #c0392b;
}

.btn-stop:hover {
  background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
}

.btn-postpone {
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  border-color: #e67e22;
}

.btn-postpone:hover {
  background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
  box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
}

/* Timer Display */
.followup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.followup-header > * {
  flex-shrink: 0;
}

.followup-title {
  flex: 1 1 auto;
  min-width: 150px;
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  min-width: 70px;
  justify-content: center;
}

.timer-blue {
  background: #e3f2fd;
  color: #1976d2;
  border: 2px solid #1976d2;
}

.timer-red {
  background: #ffebee;
  color: #c62828;
  border: 2px solid #c62828;
  animation: pulse-timer 1.5s infinite;
}

@keyframes pulse-timer {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.timer-icon {
  font-size: 0.9rem;
}

.timer-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

/* Responsive */
@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }

  .btn-compact {
    width: 100%;
    justify-content: center;
  }

  .actions-buttons {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .actions-buttons {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
